// Combat, enemy, projectile and pickup contracts for the 2D platformer.
// Pure types plus a few pure helpers: no three.js, no DOM, no React, so
// `platformer2d.ts` (which consumes these) stays deterministic and
// unit-testable. Rendering of everything here lives in
// `Platformer2DScene.tsx`.
import type { Sprite2D } from '../../content/schema/map';

/** Which side spawned a projectile, and therefore whom it can hurt. */
export type CombatSide = 'player' | 'enemy';

/**
 * A ranged attack's projectile. Distances are in tiles, speeds in tiles per
 * second. A returning projectile flies out to `range` then reverses toward
 * its owner and despawns on reaching them (a boomerang); a non-returning
 * one despawns at `range` or on hitting a solid tile.
 */
export interface ProjectileSpec {
  speed: number;
  range: number;
  damage: number;
  /** Collision radius in tiles. */
  radius: number;
  returns?: boolean;
  /** Tiles per second squared of downward acceleration; 0 flies straight. */
  gravity?: number;
  /** Optional baked art for the projectile (single frame or clip-bearing). */
  art?: Sprite2D;
  /** Free-form id a renderer can key FX and audio on ('moon-shard', 'ember'). */
  kind?: string;
}

/**
 * A weapon the player can equip. `melee` swings a hitbox in front of the
 * player; `ranged` fires a `projectile`; `charge` is held to charge, then
 * released into a short forward lunge whose hitbox travels with the player.
 */
export interface WeaponSpec {
  id: string;
  kind: 'melee' | 'ranged' | 'charge';
  damage: number;
  /** Melee/charge reach in tiles, measured forward from the player centre. */
  reach: number;
  /** Melee/charge hitbox half-height in tiles. */
  halfHeight: number;
  /** How long the hitbox stays active (melee, charge) or the fire animation
   * locks the next shot (ranged). */
  activeSeconds: number;
  cooldownSeconds: number;
  /** Horizontal knockback applied to an enemy that is hit. */
  knockback: number;
  /** Seconds an enemy hit by this weapon is stunned (0 = none). */
  stunSeconds: number;
  /** Seconds the attack button must be held before a charge weapon fires. */
  chargeSeconds?: number;
  /** Tiles the player lunges during a charge weapon's active window. */
  lungeDistance?: number;
  projectile?: ProjectileSpec;
}

/** Enemy behaviour archetypes. Every one telegraphs before it hurts. */
export type EnemyBehaviour =
  /** Walks its patrol range, turns at edges and walls. Contact damage. */
  | 'patrol'
  /** Waits, then hops toward the player in arcs. Contact damage. */
  | 'hop'
  /** Flies along a horizontal patrol with a sine bob; dives when close. */
  | 'flyer'
  /** Static; telegraphs, then fires a projectile at the player. */
  | 'turret'
  /** Waits, telegraphs, then charges horizontally until it hits a wall. */
  | 'charger'
  /** Invulnerable while facing the player; turns slowly. Hit from behind. */
  | 'shield'
  /** Multi-phase: charge, slam, volley. HP and phase counts come from `boss`. */
  | 'boss';

export interface BossSpec {
  /** Phases cycle in order; a phase ends when its duration elapses. */
  phases: ('charge' | 'slam' | 'volley' | 'rest')[];
  /** Seconds each phase lasts. */
  phaseSeconds: number;
  /** Projectiles per volley. */
  volleyCount: number;
}

export interface EnemyDef {
  behaviour: EnemyBehaviour;
  hp: number;
  contactDamage: number;
  /** Movement speed in tiles per second (charge speed for chargers). */
  speed: number;
  /** Collision half-extents in tiles. */
  halfWidth: number;
  halfHeight: number;
  /** Tiles within which the enemy notices the player. */
  aggroRange: number;
  /** Seconds between a telegraph starting and the dangerous move landing. */
  telegraphSeconds: number;
  /** Seconds after an attack before the enemy can attack again. */
  attackCooldownSeconds: number;
  /** Horizontal patrol range in tiles for walkers and flyers. */
  minX?: number;
  maxX?: number;
  /** Flyers ignore gravity and tiles; everyone else collides with solids. */
  flying?: boolean;
  /** Bob amplitude in tiles for flyers. */
  bobAmplitude?: number;
  /** Hop impulse for hoppers, tiles per second. */
  hopImpulse?: number;
  projectile?: ProjectileSpec;
  boss?: BossSpec;
  /** Points awarded or an arbitrary tag a game can read on `enemy-death`. */
  tag?: string;
}

export type EnemyState =
  | 'idle'
  | 'move'
  | 'telegraph'
  | 'attack'
  | 'recover'
  | 'hurt'
  | 'stunned'
  | 'dead';

/** Live per-enemy state, keyed by the `DynamicEntity.id` that spawned it. */
export interface EnemyRuntime {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  hp: number;
  facing: 'left' | 'right';
  state: EnemyState;
  /** Seconds remaining in the current state, where the state is timed. */
  stateTimer: number;
  cooldownTimer: number;
  /** Seconds of hurt flash remaining; purely visual. */
  hurtFlash: number;
  isGrounded: boolean;
  /** Boss phase index; 0 for every other behaviour. */
  phase: number;
  /** Seconds since the enemy died; the renderer plays the death clip. */
  deathTimer: number;
  /** Bob or hop phase accumulator, seconds. */
  clock: number;
}

export interface Projectile {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  owner: CombatSide;
  damage: number;
  radius: number;
  /** Distance travelled so far, tiles. */
  travelled: number;
  spec: ProjectileSpec;
  returning: boolean;
  /** Enemy ids already damaged by this projectile (no double hits). */
  hitIds: string[];
  /** Origin x/y so a returning projectile knows where home is. */
  originX: number;
  originY: number;
}

export type PickupKind = 'weapon' | 'health' | 'collectible' | 'ability';

export interface PickupDef {
  kind: PickupKind;
  /** The weapon id, ability id, or collectible id being granted. */
  itemId: string;
  /** Health restored when `kind` is 'health'. */
  amount?: number;
}

/** The player's active attack, if any. */
export interface AttackState {
  weaponId: string;
  /** Seconds remaining in the active window. */
  timer: number;
  duration: number;
  facing: 'left' | 'right';
  /** Enemy ids already hit by this swing. */
  hitIds: string[];
  kind: WeaponSpec['kind'];
}

/** Axis-aligned box helper shared by attacks, enemies and projectiles. */
export function boxesOverlap(
  ax: number,
  ay: number,
  ahw: number,
  ahh: number,
  bx: number,
  by: number,
  bhw: number,
  bhh: number,
): boolean {
  return Math.abs(ax - bx) < ahw + bhw && Math.abs(ay - by) < ahh + bhh;
}

/** The hitbox a melee or charge weapon covers, in tiles, for a given facing. */
export function meleeHitbox(
  weapon: WeaponSpec,
  x: number,
  y: number,
  facing: 'left' | 'right',
): { x: number; y: number; hw: number; hh: number } {
  const dir = facing === 'right' ? 1 : -1;
  const hw = weapon.reach / 2;
  return { x: x + dir * (0.2 + hw), y, hw, hh: weapon.halfHeight };
}

/** Create the live state for an enemy from its definition and spawn point. */
export function createEnemyRuntime(
  id: string,
  def: EnemyDef,
  spawn: { x: number; y: number },
  facing: 'left' | 'right' = 'left',
): EnemyRuntime {
  return {
    id,
    x: spawn.x,
    y: spawn.y,
    vx: 0,
    vy: 0,
    hp: def.hp,
    facing,
    state: 'idle',
    stateTimer: 0,
    cooldownTimer: 0,
    hurtFlash: 0,
    isGrounded: false,
    phase: 0,
    deathTimer: 0,
    clock: 0,
  };
}
